class CartPage extends HTMLElement {
    constructor() {
      super();
  
      this._selectors = {
        form: "[js-cart-form]",
        header: "#header-container",
        qtyInput: "[js-qty-input]",
      };
    }
  
    connectedCallback() {
      this.form = this.querySelector(this._selectors.form);
      this.header = document.querySelector(this._selectors.header);
      this.qtyInputs = this.querySelectorAll(this._selectors.qtyInput);
      document.addEventListener("QTYChange", this.handleQuantityChange.bind(this));
  
      this.qtyInputs.forEach((input) => {
        input.addEventListener("change", this.handleQuantityChange.bind(this));
      });
  
      this._setVariables();
      this._watchWindowResize();
    }
  
    _watchWindowResize = (evt) => {
      window.addEventListener("resize", this._setVariables);
    };
  
    _setVariables = (evt) => {
      document.documentElement.style.setProperty(
        "--header-height",
        `${this.header.clientHeight}px`
      );
    };
  
    handleQuantityChange(e) {
      if (this.form) {
        this.form.submit();
      }
    }
}

customElements.define("cart-page", CartPage);

class MobileMenu extends HTMLElement {
  constructor() {
    super();

    this._selectors = {
      container: "[js-container]",
      triggers: "[js-trigger]",
      back: "[js-back]",
      titles: "[js-title]",
      parents: "[js-parent]",
      linkChildren: "[js-child]",
      linkGrandchildren: "[js-grandchild]",
      headerBack: "[js-mobile-menu-back-btn]",
    };

    this._level = 0;
    this._activeParentId = null;
  }

  connectedCallback() {
    this.container = this.querySelector(this._selectors.container);
    if (!this.container) return;

    this.triggers = this.querySelectorAll(this._selectors.triggers);
    this.backs = this.querySelectorAll(this._selectors.back);
    this.titles = this.querySelectorAll(this._selectors.titles);
    this.parents = this.querySelectorAll(this._selectors.parents);
    this.linkChildren = this.querySelectorAll(this._selectors.linkChildren);
    this.linkGrandchildren = this.querySelectorAll(
      this._selectors.linkGrandchildren
    );
    this.headerBackBtn = this.querySelector(this._selectors.headerBack);
    this.headerBackText = this.headerBackBtn?.querySelector(
      ".mobile-nav-drawer__back-text"
    );
    this.panels = this.querySelectorAll(".mobile-nav__slider-panel");

    this.linkChildren.forEach((child) => this._hide(child));
    this.linkGrandchildren.forEach((grandchild) => this._hide(grandchild));

    this._setEventListeners();
    this._watchDrawerClose();
    this._syncInactivePanels(0);
  }

  disconnectedCallback() {
    this._drawerObserver?.disconnect();
  }

  _setEventListeners() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", this._slideToChild);
    });

    this.headerBackBtn?.addEventListener("click", this._headerBackOnClick);
  }

  _watchDrawerClose() {
    this._drawerWasOpen = document.body.classList.contains("js-drawer-open-left");

    this._drawerObserver = new MutationObserver(this._onDrawerClassChange);
    this._drawerObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  _onDrawerClassChange = () => {
    const isOpen = document.body.classList.contains("js-drawer-open-left");

    if (this._drawerWasOpen && !isOpen) {
      this._navigateToLevel(0, null, { moveFocus: false });
    }

    this._drawerWasOpen = isOpen;
  };

  _headerBackOnClick = (evt) => {
    evt.preventDefault();
    if (this._level <= 0) return;

    if (this._level === 2) {
      this._navigateToLevel(1, this._activeParentId);
      return;
    }

    this._navigateToLevel(0, null);
  };

  _slideToChild = (evt) => {
    evt.preventDefault();
    const level = Number(evt.currentTarget.getAttribute("data-level"));
    const id = evt.currentTarget.getAttribute("data-id");
    this._navigateToLevel(level, id);
  };

  _hide(el) {
    el.classList.remove("is-active");
    el.setAttribute("hidden", "");
  }

  _show(el) {
    el.classList.add("is-active");
    el.removeAttribute("hidden");
  }

  _setTitle(id) {
    if (!this.headerBackText) return;

    const title = Array.from(this.titles).find(
      (el) => el.getAttribute("data-id") === id
    );

    if (title) {
      this.headerBackText.textContent = title.textContent.trim();
    }
  }

  _navigateToLevel(level, id, { moveFocus = true } = {}) {
    const translate =
      level > 0 ? `translateX(${((-100 / 3) * level).toFixed(4)}%)` : null;

    switch (level) {
      case 1:
        this._activeParentId = id;

        this.backs.forEach((back) => {
          back.setAttribute("data-id", id);
        });

        this._setTitle(id);

        this.linkChildren.forEach((child) => {
          this._hide(child);

          if (child.getAttribute("data-id") === id) {
            this._show(child);
          }
        });

        this.parents.forEach((parent) => {
          if (parent.getAttribute("aria-hidden") === "false") {
            parent.style.display = "none";
          }
        });

        this.linkGrandchildren.forEach((grandchild) => {
          this._hide(grandchild);
        });
        break;

      case 2:
        this._setTitle(id);

        this.linkGrandchildren.forEach((grandchild) => {
          this._hide(grandchild);

          if (grandchild.getAttribute("data-id") === id) {
            this._show(grandchild);
          }
        });
        break;

      default:
        this._activeParentId = null;

        this.parents.forEach((parent) => {
          if (parent.getAttribute("aria-hidden") === "false") {
            parent.removeAttribute("style");
          }
        });

        this.linkChildren.forEach((child) => {
          this._hide(child);
        });

        this.linkGrandchildren.forEach((grandchild) => {
          this._hide(grandchild);
        });

        if (this.headerBackText) {
          this.headerBackText.textContent = "BACK";
        }
        break;
    }

    if (translate) {
      this.container.style.transform = translate;
    } else {
      this.container.removeAttribute("style");
    }

    this._level = level;
    this.dataset.active = level > 0 ? "true" : "false";

    this._resetPanelScroll(level);
    this._syncInactivePanels(level);

    if (moveFocus) {
      this._focusActivePanel(level);
    }
  };

  _syncInactivePanels(level) {
    this.panels?.forEach((panel, index) => {
      const isActive = index === level;

      if (isActive) {
        panel.removeAttribute("inert");
        panel.removeAttribute("aria-hidden");
        return;
      }

      panel.setAttribute("inert", "");
      panel.setAttribute("aria-hidden", "true");
    });
  }

  _focusActivePanel(level) {
    const panel = this.panels?.[level];
    if (!panel) return;

    const focusable = panel.querySelectorAll("a[href], button:not([disabled])");
    const target =
      Array.from(focusable).find((el) => !el.closest("[hidden]")) ||
      this.headerBackBtn;

    if (!target || typeof target.focus !== "function") return;

    target.focus({ preventScroll: true });
  }

  _resetPanelScroll(level) {
    const panel = this.panels?.[level];

    if (!panel) return;

    panel.scrollTop = 0;
  }
}

customElements.define("s-mobile-menu", MobileMenu);